"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { SlotDesign } from "src/components/greeting-card/design-data";
import { readDraft } from "./card-draft-store";
import { createDraftSaver } from "./create-draft-saver";

const NOOP = {
  onChange: (_: SlotDesign[]) => {},
  flush: () => {},
  clear: () => {},
};

export function useCardDraft(key: string | null): {
  restored: SlotDesign[] | null;
  onChange: (slots: SlotDesign[]) => void;
  clear: () => void;
} {
  // Read the draft exactly once, before any autosave write (invariant #1/#2).
  const [restored] = useState<SlotDesign[] | null>(() =>
    key ? readDraft(key, Date.now()) : null,
  );

  const saver = useMemo(() => (key ? createDraftSaver(key) : NOOP), [key]);
  const saverRef = useRef(saver);
  saverRef.current = saver;

  // No lost tail: flush pending writes when the tab hides or the component unmounts.
  useEffect(() => {
    const flush = () => saverRef.current.flush();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, []);

  return {
    restored,
    onChange: saver.onChange,
    clear: saver.clear,
  };
}
